"use client"

import { useState } from "react"
import { Languages } from "lucide-react"
import { useLocale } from "@/components/LocaleProvider"

/**
 * Client-side translation for user-generated content.
 * Calls Google's translation API directly from the browser (allowed by CORS),
 * which avoids server-side 403 blocks. Falls back gracefully if unavailable.
 */
export function TranslateButton({
  text,
  onTranslated,
}: {
  text: string
  onTranslated: (translated: string | null) => void
}) {
  const { locale, t } = useLocale()
  const [loading, setLoading] = useState(false)
  const [isTranslated, setIsTranslated] = useState(false)

  // Don't show if locale is English (content is already in English)
  if (locale === "en") return null

  async function handleClick() {
    if (isTranslated) {
      onTranslated(null)
      setIsTranslated(false)
      return
    }

    setLoading(true)
    try {
      // Prefer our server endpoint so results are cached in the DB (translate
      // once per string + language) and use the official API when configured.
      let translated: string | null = null
      try {
        const res = await fetch("/api/translate", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ text, targetLang: locale }),
        })
        if (res.ok) {
          const data = await res.json()
          if (data.translated && data.translated !== text) translated = data.translated
        }
      } catch {
        // fall through to client-side fallback below
      }

      // Fallback: if the server couldn't translate (e.g. no API key yet and the
      // free endpoint 403s server-side), call it directly from the browser —
      // the original behavior — so translation keeps working with zero regression.
      if (!translated) {
        const sourceLang = locale === "es" ? "en" : "es"
        const url = `https://translate.googleapis.com/translate_a/single?client=gtx&sl=${sourceLang}&tl=${locale}&dt=t&q=${encodeURIComponent(text)}`
        const res = await fetch(url)
        if (res.ok) {
          const data = await res.json()
          const t = (data[0] as Array<[string]>).map((s: [string]) => s[0]).join("")
          if (t && t !== text) translated = t
        }
      }

      if (translated) {
        onTranslated(translated)
        setIsTranslated(true)
      }
    } catch {
      // silently fail — user can retry
    } finally {
      setLoading(false)
    }
  }

  return (
    <button
      onClick={handleClick}
      disabled={loading}
      className="inline-flex items-center gap-1.5 text-xs text-[var(--muted-foreground)] hover:text-[var(--foreground)] transition-colors disabled:opacity-50"
    >
      <Languages size={13} />
      {loading
        ? "..."
        : isTranslated
        ? t("residency.showOriginal")
        : t("residency.translateDescription")}
    </button>
  )
}
